// LxTable — Lamtix-token-wrapped Table primitive
//
// RTL audit:
//   - th/td use text-start (logical, RTL-safe) not text-left
//   - Numeric cells: caller applies .numeric class (exported LxTableNumericCell helper)
//   - Action column: physical left in RTL (place at end of <tr>) — caller responsibility
//   - Table scroll origin is correct by default in RTL (no override needed)
//   - No raw Tailwind colors — all via shadcn bridge (bg-muted, text-muted-foreground)
//
// D-SHADCN-LAMTIX-WRAPPED-01: sole import path is @lamtix/ui
import * as React from 'react';
import { cn } from '../utils';
import {
  Table,
  TableBody,
  TableCaption,
  TableCell,
  TableFooter,
  TableHead,
  TableHeader,
  TableRow,
} from '../shadcn/table';

// ── LxTable ──────────────────────────────────────────────────────────────────

export interface LxTableProps extends React.HTMLAttributes<HTMLTableElement> {
  caption?: string;
}

const LxTable = React.forwardRef<HTMLTableElement, LxTableProps>(
  ({ className, caption, children, ...props }, ref) => (
    <Table ref={ref} className={cn('text-sm', className)} {...props}>
      {caption && <TableCaption>{caption}</TableCaption>}
      {children}
    </Table>
  ),
);
LxTable.displayName = 'LxTable';

// ── LxTableHeader ────────────────────────────────────────────────────────────

const LxTableHeader = React.forwardRef<
  HTMLTableSectionElement,
  React.HTMLAttributes<HTMLTableSectionElement>
>(({ className, ...props }, ref) => (
  <TableHeader
    ref={ref}
    className={cn('bg-lx-neutral-50 text-muted-foreground', className)}
    {...props}
  />
));
LxTableHeader.displayName = 'LxTableHeader';

// ── LxTableBody ──────────────────────────────────────────────────────────────

const LxTableBody = React.forwardRef<
  HTMLTableSectionElement,
  React.HTMLAttributes<HTMLTableSectionElement>
>(({ className, ...props }, ref) => (
  <TableBody ref={ref} className={cn('divide-y divide-border', className)} {...props} />
));
LxTableBody.displayName = 'LxTableBody';

// ── LxTableRow ───────────────────────────────────────────────────────────────

const LxTableRow = React.forwardRef<HTMLTableRowElement, React.HTMLAttributes<HTMLTableRowElement>>(
  ({ className, ...props }, ref) => (
    // MCP: row hover --lx-primary-50 (branded hover, not neutral)
    <TableRow
      ref={ref}
      className={cn(
        'border-b border-border transition-colors hover:bg-lx-primary-50 data-[state=selected]:bg-lx-primary-100',
        className,
      )}
      {...props}
    />
  ),
);
LxTableRow.displayName = 'LxTableRow';

// ── LxTableHead — th cell with text-start (RTL-safe) ─────────────────────────

const LxTableHead = React.forwardRef<
  HTMLTableCellElement,
  React.ThHTMLAttributes<HTMLTableCellElement>
>(({ className, ...props }, ref) => (
  <TableHead
    ref={ref}
    // MCP: header padding 12px vertical / 14px horizontal
    className={cn(
      'px-[14px] py-3 text-start text-xs font-semibold uppercase tracking-wide text-muted-foreground',
      className,
    )}
    {...props}
  />
));
LxTableHead.displayName = 'LxTableHead';

// ── LxTableCell — td cell with text-start (RTL-safe) ─────────────────────────

const LxTableCell = React.forwardRef<
  HTMLTableCellElement,
  React.TdHTMLAttributes<HTMLTableCellElement>
>(({ className, ...props }, ref) => (
  <TableCell
    ref={ref}
    // MCP: cell padding 14px both axes
    className={cn('px-[14px] py-[14px] text-start align-middle text-foreground', className)}
    {...props}
  />
));
LxTableCell.displayName = 'LxTableCell';

// ── LxTableNumericCell — td for numeric content (D-NUMBERS-EN-01) ─────────────
// Always LTR + tabular-nums. Use for IDs, counts, dates, codes.

const LxTableNumericCell = React.forwardRef<
  HTMLTableCellElement,
  React.TdHTMLAttributes<HTMLTableCellElement>
>(({ className, ...props }, ref) => (
  <TableCell
    ref={ref}
    className={cn('px-[14px] py-[14px] align-middle numeric', className)}
    {...props}
  />
));
LxTableNumericCell.displayName = 'LxTableNumericCell';

// ── LxTableFooter ────────────────────────────────────────────────────────────

const LxTableFooter = React.forwardRef<
  HTMLTableSectionElement,
  React.HTMLAttributes<HTMLTableSectionElement>
>(({ className, ...props }, ref) => (
  <TableFooter ref={ref} className={cn('bg-lx-neutral-50 text-muted-foreground', className)} {...props} />
));
LxTableFooter.displayName = 'LxTableFooter';

export {
  LxTable,
  LxTableHeader,
  LxTableBody,
  LxTableRow,
  LxTableHead,
  LxTableCell,
  LxTableNumericCell,
  LxTableFooter,
};
